import type * as React from 'react';

import type { DescriptionPage_ProductVariant as DescriptionPageProductVariant } from '__generated__/graphql-types';
import { branch, compose, withProps } from 'recompose';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import connectToRouter from 'js/lib/connectToRouter';
import user from 'js/lib/user';

import type { ButtonProps } from '@coursera/cds-core';

import { useFreeTrialCTACopyExperimentData } from 'bundles/coursera-plus/components/elevateLTV/startFreeTrial/useGenerateStartFreeTrialTestEpicImpression';
import withCourseraPlusProductEligibilityCheck from 'bundles/coursera-plus/utils/withCourseraPlusProductEligibilityCheck';
import type { EligibilityCheckProps } from 'bundles/coursera-plus/utils/withCourseraPlusProductEligibilityCheck';
import type { PropsFromCourseraPlusProductOwnerships } from 'bundles/coursera-plus/utils/withCourseraPlusProductOwnerships';
import withCourseraPlusProductOwnerships from 'bundles/coursera-plus/utils/withCourseraPlusProductOwnerships';
import {
  getBannerTitle,
  getBannerTitleComponent,
  getEnrollButtonLabel,
  getEnrollButtonTrackingName,
  getIsEnrollButtonDisabled,
  getIsEnrolled,
  getPreEnrollmentFlowVariant,
  getPriceString,
  getShowOpenAidModal,
  getSubtitleElement,
} from 'bundles/enroll/components/xdp/enrollSectionUtils';
import withEnrollModal from 'bundles/enroll/components/xdp/withEnrollModal';
import withEnrollment from 'bundles/enroll/components/xdp/withEnrollment';
import { withPrice } from 'bundles/enroll/components/xdp/withNaptimeData';
import type { Course } from 'bundles/enterprise-legacy-learner-home/types/xdpTypes';
import BannerEnroll from 'bundles/enterprise-legacy-xdp/components/conversion/BannerEnroll';
import Naptime from 'bundles/naptimejs';
import CoursesV1 from 'bundles/naptimejs/resources/courses.v1';
import OnDemandSpecializationsV1 from 'bundles/naptimejs/resources/onDemandSpecializations.v1';
import {
  withFinaidByUserAndProducts,
  withSpecializations,
} from 'bundles/payments/components/finaid-2021/enhancers/withCoursesAndFinancialAidApplications';
import { withFinancialAidApplicationsByUserAndProduct } from 'bundles/payments/components/finaid-2021/enhancers/withFinancialAidApplications';
import withPromotionInfo from 'bundles/promotions/components/withPromotionInfo';
import useCheckS12nUpgradeEligibility from 'bundles/s12n-common/components/s12n-versioning-v2/useCheckS12nUpgradeEligibility';
import type { S12nProductVariant } from 'bundles/s12n-common/constants/s12nProductVariants';
import type ApplicationStoreType from 'bundles/ssr/stores/ApplicationStore';

const WithEnrollModal = withEnrollModal(BannerEnroll);

type PropsFromCaller = {
  onlyShowButton?: boolean;
  hideFinaidLink?: boolean;
  isEligibleForPlusMonthlyToAnnualUpgrade: boolean;
  isCourseraPlusEligibleProduct?: boolean;
  showUndoCancellationCTA?: boolean;
  accentColor?: string;
  isSpecialization: boolean;
  courseCount?: number;
  courseId?: string;
  s12nId?: string;
  partnerIds?: string[];
  s12nProductVariant?: S12nProductVariant | DescriptionPageProductVariant;
  name?: string;
  slug?: string;
  buttonMinHeightInPixels?: number;
  buttonStyle?: React.CSSProperties;
  enrollBtnCustomStyles?: React.CSSProperties;
  course?: CoursesV1 | Course;
  onStickyHeader?: boolean;
  wrapperClassName?: React.CSSProperties; // used in BannerEnroll
  buttonRef?: React.RefCallback<HTMLButtonElement>;
};

type PropsToComponent = PropsFromCourseraPlusProductOwnerships & EligibilityCheckProps;

export const withEnrollButtonData = <InputProps extends PropsFromCaller>(withEnrollmentNotRequired?: boolean) =>
  compose<PropsToComponent, InputProps>(
    connectToStores<Pick<ApplicationStoreType, 'userAgent'>, ApplicationStoreType>(
      ['ApplicationStore'],
      ({ ApplicationStore }) => ({
        userAgent: ApplicationStore.getUserAgent(),
      })
    ),
    connectToRouter((router) => ({ router })),
    // Need to call CoursesV1 to check if course is launched already or not as well withEnrollment to render enroll button on SSR
    Naptime.createContainer(({ courseId }: { courseId: string }) => ({
      course: CoursesV1.get(courseId, {
        fields: ['courseStatus', 'premiumExperienceVariant', 'certificates', 's12nIds', 'partnerIds'],
        params: {
          showHidden: true,
        },
        required: false,
      }),
    })),
    // Need to call OnDemandSpecializationsV1 to check if s12n is pre enrollment enabled when the learner is logged out
    Naptime.createContainer(
      ({ course, s12nId, isSpecialization }: { course?: CoursesV1; s12nId?: string; isSpecialization?: boolean }) => ({
        s12ns:
          !isSpecialization &&
          course &&
          course.s12nIds &&
          OnDemandSpecializationsV1.multiGet(course.s12nIds, {
            fields: ['preEnrollmentEnabledAt', 'plannedLaunchDate', 'launchedAt'],
            required: false,
          }),
        s12n:
          isSpecialization &&
          s12nId &&
          OnDemandSpecializationsV1.get(s12nId || '', {
            fields: ['preEnrollmentEnabledAt', 'plannedLaunchDate', 'launchedAt', 'partnerIds'],
            required: false,
          }),
      })
    ),
    withEnrollment(withEnrollmentNotRequired),
    branch(
      () => typeof window !== 'undefined' && user.isAuthenticatedUser(),
      // All of these happen on client and should be non-blocking
      compose(
        withPrice(true),
        withPromotionInfo(true),
        branch<PropsFromCaller>(
          (props) => Boolean(props.isSpecialization && props.s12nId),
          compose(withSpecializations(true), withFinaidByUserAndProducts(true)),
          withFinancialAidApplicationsByUserAndProduct(undefined, true)
        ),
        branch<PropsFromCaller>(
          ({ isCourseraPlusEligibleProduct }) => !isCourseraPlusEligibleProduct,
          withCourseraPlusProductEligibilityCheck(true)
        ),
        withCourseraPlusProductOwnerships(true)
      )
    ),
    withProps(
      ({
        enrollmentAvailableChoices,
        productPrice,
        s12nDerivative,
        multiS12nDerivatives,
        isSpecialization,
        s12nId,
        course,
        courseId,
        courseCount,
        onlyShowButton,
        name,
        isCourseraPlusEligibleProduct,
        financialAidApplicationsByUserAndProduct,
        financialAidApplications,
        productVariant,
        courses,
        router,
        isEligibleForPlusMonthlyToAnnualUpgrade,
        showUndoCancellationCTA,
        s12n,
        s12ns,
        ...rest
      }: {
        [x: string]: any;
      }) => {
        const isEnrolled = getIsEnrolled({ enrollmentAvailableChoices, isSpecialization });
        const isCapstoneAccessLocked = enrollmentAvailableChoices?.isCapstoneAccessLocked;
        const canEnrollThroughCourseraPlus = enrollmentAvailableChoices?.canEnrollThroughCourseraPlus;
        const preEnrollmentFlowVariant = getPreEnrollmentFlowVariant({
          enrollmentAvailableChoices,
        });

        const priceString = getPriceString({
          multiS12nDerivatives,
          s12nDerivative,
          productPrice,
        });
        const TitleComponent = getBannerTitleComponent({
          enrollmentAvailableChoices,
          onlyShowButton,
          preEnrollmentFlowVariant,
        });
        const isStandAloneCourse = !isSpecialization && (!s12ns || s12ns.length < 1);
        const { isUserBasedFreeTrialCopyEnabled, isSessionBasedFreeTrialCopyEnabled } =
          useFreeTrialCTACopyExperimentData({
            enrollmentAvailableChoices,
            isStandAloneCourse,
          });

        const isStartFreeTrialExperimentCopyEnabled =
          isUserBasedFreeTrialCopyEnabled || isSessionBasedFreeTrialCopyEnabled;
        const title = getBannerTitle({
          priceString,
          enrollmentAvailableChoices,
          isSpecialization,
          onlyShowButton,
          preEnrollmentFlowVariant,
          isStartFreeTrialExperimentCopyEnabled,
        });

        const subtitleElem = getSubtitleElement({
          onlyShowButton,
          isCapstoneAccessLocked,
          courseCount,
          isEnrolled,
          canEnrollThroughCourseraPlus,
          courseName: name,
        });

        const buttonSize: ButtonProps['size'] = onlyShowButton ? 'small' : 'medium';
        const trackingName = onlyShowButton ? 'sticky_header' : 'top_banner';

        const { openFinAidInfo, showOpenAidModal } = getShowOpenAidModal({
          financialAidApplications: financialAidApplications?.elements,
          financialAidApplicationsByUserAndProduct,
          isSpecialization,
          s12nId,
          courses: courses?.elements,
        });
        const eligibilityData = useCheckS12nUpgradeEligibility({ s12nId, isSpecialization });
        const isS12nUpgradeEligible = eligibilityData?.upgradeEligibility ?? false;
        const currentS12nId = eligibilityData?.currentEnrolledS12nInFamily?.id ?? '';
        const lastActiveCourseSlug = eligibilityData?.currentEnrolledS12nInFamily?.lastActiveCourse?.slug ?? '';

        // TODO (htran) figure out why the test fails on initial run with this prop missing
        if (!course) {
          return rest;
        }
        return {
          isEnrolled,
          title,
          subtitleElem,
          buttonSize,
          courseId,
          buttonLabel: getEnrollButtonLabel({
            enrollmentAvailableChoices,
            isSpecialization,
            course,
            preEnrollmentFlowVariant,
            s12nId,
            s12n,
            s12ns,
            pathname: router.location.pathname,
            isEligibleForPlusMonthlyToAnnualUpgrade,
            showUndoCancellationCTA,
            isS12nUpgradeEligible,
            isFreeTrialCopyEnabled: isStartFreeTrialExperimentCopyEnabled,
          }),
          buttonDisabled: getIsEnrollButtonDisabled({
            enrollmentAvailableChoices,
            isSpecialization,
            course,
          }),
          buttonTrackingName: getEnrollButtonTrackingName({ enrollmentAvailableChoices }),
          isSpecialization,
          // `onlyShowButton` is only true for the sticky SCDP header
          // don't want to check params for that because then we will duplicate opening the enroll modal with the main enroll section
          shouldCheckActionParams: !onlyShowButton,
          showOpenAidModal,
          openFinAidInfo,
          isEligibleForPlusMonthlyToAnnualUpgrade,
          isCourseraPlusEligibleProduct,
          trackingName,
          productVariant,
          s12nProductVariant: productVariant,
          enrollmentAvailableChoices,
          router,
          TitleComponent,
          isS12nUpgradeEligible,
          currentS12nId,
          ...rest,
          partnerIds: isSpecialization ? s12n?.partnerIds : course?.partnerIds,
          lastActiveCourseSlug,
        };
      }
    )
  );

// @ts-expect-error TSMIGRATION
export default withEnrollButtonData(true)(WithEnrollModal);
